import React from 'react';
import { GradientBorder } from 'components/common/GradientBorder';
import { Markdown } from 'components/common/Markdown';
import { gradients } from 'theme/tokens';
import { transformBias, transformTradeIdeas, transformWarnings, transformInvalidation, resolveBiasIsBullish } from 'utils/brainerAnalysisTransform';
import type { AnalysisResultItem } from 'types/analysis';
import './analysisView.css';

interface AnalysisViewProps {
	result: AnalysisResultItem;
	/**
	 * Live price from the panel's WS subscription, if connected. Falls back to the
	 * analysis-snapshot price (`result.response.price`) when absent so the strip
	 * always renders.
	 */
	livePrice?: number | null;
}

function formatPrice(value: number): string {
	return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** Deep-analysis (Stage 4) render: bias, trade ideas, warnings, invalidation. */
export function AnalysisView({ result, livePrice }: AnalysisViewProps) {
	// v0 direct-vision engine returns free prose, not StyleJson — there are no
	// trades/levels to lay out, so render the text itself (the "graphic" view just
	// shows the formatted analysis).
	if (result.response?.format === 'prose' || result.response?.prose != null) {
		return (
			<div className='analysis-view'>
				<div className='av-bias-wrap'>
					<GradientBorder colors={gradients.borderDefault} borderRadius={10} borderWidth={2}>
						<div className='av-prose'>
							<Markdown>{result.response.prose ?? '— no analysis —'}</Markdown>
						</div>
					</GradientBorder>
				</div>
			</div>
		);
	}

	const bias = transformBias(result);
	const isBullishBias = resolveBiasIsBullish(result);
	const tradeIdeas = transformTradeIdeas(result);
	const warnings = transformWarnings(result);
	const invalidation = transformInvalidation(result);

	const hasLive = livePrice != null && Number.isFinite(livePrice);
	const currentPrice = hasLive ? (livePrice as number) : result.response.price;

	return (
		<div className='analysis-view'>
			{/* ── Bias (full width) ── */}
			<div className='av-bias-wrap'>
				<GradientBorder colors={isBullishBias ? gradients.borderLong : gradients.borderShort} borderRadius={10} borderWidth={2}>
					<div className='av-bias-top'>
						<div className='av-bias-label'>
							BIAS · {bias.timeframe} · {bias.pair}
						</div>
						<div className='av-bias-direction' style={{ color: isBullishBias ? 'var(--green)' : 'var(--red)' }}>
							{bias.direction}
						</div>
						<div className='av-bias-sub'>{bias.sub}</div>
						<div className='av-bias-ctx'>
							{bias.contextLines.map((line, i) => (
								<div key={i} className='av-bias-ctx-line'>
									{line}
								</div>
							))}
							<div className='av-bias-ctx-alert'>{bias.contextAlert}</div>
						</div>
					</div>
					<div className='av-bias-summary-wrap'>
						<div className='av-bias-summary'>{bias.summary}</div>
					</div>
				</GradientBorder>
			</div>

			{/* ── Trade ideas + warnings + invalidation flow into the masonry grid ── */}
			<div className='panel-grid'>
				{tradeIdeas.map((idea, i) => {
					const isLong = idea.type === 'LONG';
					return (
						<div className='pg-item' key={`trade-${i}`}>
							<GradientBorder colors={isLong ? gradients.borderDefault : ['#FF5A5A', '#FF3D3D', '#FF5A5A']} borderRadius={10} borderWidth={2}>
								<div className='av-trade-head'>
									<div>
										<div className='av-trade-dir-label'>{idea.dirLabel}</div>
										<div className={isLong ? 'av-trade-dir-long' : 'av-trade-dir-short'}>{isLong ? '↑ Long' : '↓ Short'}</div>
									</div>
									<div className={`av-trade-badge ${idea.badgeVariant === 'best' ? 'best' : 'dec'}`}>{idea.badge}</div>
								</div>

								<div className='av-trade-rows'>
									{idea.metrics.map((m, mi) =>
										m.fullWidth ? (
											<div key={mi} className='av-trade-row-full'>
												<div className='av-trade-metric-label'>{m.label}</div>
												<div className='av-trade-metric-inline'>
													<span className='av-trade-metric-value' style={m.color ? { color: m.color } : undefined}>
														{m.value}
													</span>
													{m.extraValue && (
														<span className='av-trade-metric-value' style={m.extraColor ? { color: m.extraColor } : undefined}>
															{' → '}
															{m.extraValue}
														</span>
													)}
													{m.extraNote && <span className='av-trade-metric-note'> {m.extraNote}</span>}
												</div>
											</div>
										) : (
											<div key={mi} className='av-trade-row-half'>
												<div className='av-trade-metric-label'>{m.label}</div>
												<div className='av-trade-metric-value' style={m.color ? { color: m.color } : undefined}>
													{m.value}
												</div>
												{m.note && <div className='av-trade-metric-note'>{m.note}</div>}
											</div>
										),
									)}
								</div>
							</GradientBorder>
						</div>
					);
				})}

				{/* ── Current Price ── */}
				<div className='pg-item'>
					<GradientBorder colors={gradients.borderDefault} borderRadius={10} borderWidth={2}>
						<div className='av-current-row'>
							<div>
								<div className='av-current-label'>Current Price · {bias.pair}</div>
								<div className='av-current-value'>{formatPrice(currentPrice)}</div>
							</div>
							<div className='av-current-note'>{hasLive ? 'Live' : 'At analysis'}</div>
						</div>
					</GradientBorder>
				</div>

				{warnings.length > 0 && (
					<div className='pg-item'>
						<GradientBorder colors={gradients.borderAlerts} borderRadius={10} borderWidth={2}>
							<div className='av-warn-head'>
								<span className='av-warn-icon'>⚡</span>
								<span className='av-warn-title'>Warnings</span>
							</div>
							{warnings.map((w, i) => (
								<div key={i} className={`av-warn-item${i === warnings.length - 1 ? ' last' : ''}`}>
									<span className={`av-warn-text${w.isHigh ? ' high' : ''}`}>⚠ {w.text}</span>
								</div>
							))}
						</GradientBorder>
					</div>
				)}

				<div className='pg-item'>
					<GradientBorder colors={gradients.borderInvalidation} borderRadius={10} borderWidth={2}>
						<div className='av-invalid-row'>
							<div>
								<div className='av-invalid-label'>{invalidation.label}</div>
								<div className='av-invalid-price'>{invalidation.price}</div>
							</div>
							<div className='av-invalid-note'>{invalidation.note}</div>
						</div>
					</GradientBorder>
				</div>
			</div>
		</div>
	);
}
